/**
 * EnergyCertificateOfNeed — PF v2 native configuration UI (Wave 2, module 2b).
 *
 * Lifts the presentational markup from the legacy EnergyCertificateStep controller
 * + its 5 sub-tabs but rewrites ALL data wiring onto the PF v2 store + file hub via
 * `useEnergyCertificateForm` (codec-backed, field-granular setters) and
 * `useEnergyUploadSync` (§4b value-guarded, interaction-gated upload markers). NO
 * constate provider, NO imperative handle, NO legacy controller, NO mount effect —
 * F-B1 + the shape-reset cascade die by construction (the shape prune is a pure
 * handler, not a `[globalShapeType]` effect).
 *
 * ZERO imports from `components/pages/PurchaseFlow/*` (DoD grep gate). Reuses the
 * `purchase_flow:energy_certificate_step.*` + `additional_information_page.*` i18n
 * namespaces verbatim. Step-specific mount analytics (`OPENS_*`) are dropped (DF).
 */

import React from 'react'
import { useTranslation } from 'react-i18next'

import { InformationType } from 'constants/purchaseFlow'

import type { CartItemId } from 'models/purchaseFlowRevamp'
import type { CartMutationHandler } from '../../adapters/bridge'
import { PfSubTabConfigurator, useSubTabs } from '../../components/PfSubTabConfigurator'

import {
  isAdditionalInformationValid,
  isBuildingLayoutValid,
  isConstructionMethodValid,
  isEnergyAndHeatingValid,
  isPropertyInformationValid,
  type EnergyCertificateConfigDTO
} from './energyCertificateOfNeed.dto'
import { useEnergyCertificateConfig } from './useEnergyCertificateConfig'
import { useEnergyCertificateForm } from './useEnergyCertificateForm'
import { useEnergyUploadSync } from './useEnergyUploadSync'
import { useConfigPanelAnnouncement } from '../useConfigPanelAnnouncement'
import { AdditionalInformationPanel } from './panels/AdditionalInformationPanel'
import { BuildingLayoutPanel } from './panels/BuildingLayoutPanel'
import { ConstructionMethodPanel } from './panels/ConstructionMethodPanel'
import { PropertyInformationPanel } from './panels/PropertyInformationPanel'
import { VentilationAndHeatingPanel } from './panels/VentilationAndHeatingPanel'

export interface EnergyCertificateOfNeedProps {
  cartItemId: CartItemId
  /** Accepted for registry signature uniformity — energy cert emits no cart mutations. */
  onCartMutation?: CartMutationHandler
}

const PAGE_VALIDITY: Record<InformationType, (dto: EnergyCertificateConfigDTO) => boolean> = {
  [InformationType.PROPERTY_INFORMATION]: isPropertyInformationValid,
  [InformationType.BUILDING_LAYOUT]: isBuildingLayoutValid,
  [InformationType.CONSTRUCTION_METHOD]: isConstructionMethodValid,
  [InformationType.ENERGY_AND_HEATING]: isEnergyAndHeatingValid,
  [InformationType.ADDITIONAL_INFORMATION]: isAdditionalInformationValid,
}

/**
 * @component EnergyCertificateOfNeed
 * Native energy-certificate configuration form (5 sub-tabs via a chip strip).
 */
export const EnergyCertificateOfNeed: React.FC<EnergyCertificateOfNeedProps> = ({ cartItemId }) => {
  const { t } = useTranslation(['purchase_flow'])

  const { value } = useEnergyCertificateConfig(cartItemId)
  const form = useEnergyCertificateForm(cartItemId)
  const uploads = useEnergyUploadSync(cartItemId)
  const tabs = useSubTabs<InformationType>(InformationType.PROPERTY_INFORMATION)

  useConfigPanelAnnouncement(cartItemId, 'energy_certificate_of_need', tabs.active)

  const content: Record<InformationType, React.ReactNode> = {
    [InformationType.PROPERTY_INFORMATION]: <PropertyInformationPanel form={form} />,
    [InformationType.BUILDING_LAYOUT]: <BuildingLayoutPanel form={form} uploads={uploads} />,
    [InformationType.CONSTRUCTION_METHOD]: <ConstructionMethodPanel form={form} />,
    [InformationType.ENERGY_AND_HEATING]: <VentilationAndHeatingPanel form={form} />,
    [InformationType.ADDITIONAL_INFORMATION]: <AdditionalInformationPanel form={form} uploads={uploads} />,
  }

  return (
    <PfSubTabConfigurator
      title={t('purchase_flow:energy_certificate_step.information_fill')}
      description={t('purchase_flow:energy_certificate_step.information_fill_description')}
      activeId={tabs.active}
      onSelect={tabs.select}
      pages={Object.values(InformationType).map((type) => ({
        id: type,
        label: t(`purchase_flow:energy_certificate_step.${type.toLowerCase()}`),
        visited: tabs.visited.has(type),
        isValid: PAGE_VALIDITY[type](value),
        content: content[type],
      }))}
    />
  )
}
